/**
 * Typed same-origin fetch client for quill-admin-backend, reached through the
 * dev-server proxy seam (webpack.config.js: context ['/api/admin/quill']) which
 * injects `x-internal-api-key` / `x-forwarded-admin-email` server-side. This
 * client therefore does NOT set any Authorization header and does NOT use
 * axios — auth is entirely the proxy's job (dev today; a server-side proxy
 * route in admin-server-enhanced.js in production, same pattern).
 *
 * Response envelope: quill-admin-backend routes mostly reply
 * `{ success: true, data: T }` (unwrap via `unwrap<T>`), but several endpoints
 * are FLAT (no `data` key) — `unwrap<T>` falls back to returning the whole body
 * in that case. See per-endpoint comments below; each has been checked against
 * the actual route source in
 * /Users/grant/LifeScribe/quill-app/services/quill-admin-backend/src/routes/*.js.
 *
 * Weight-matrix / interview-bank / coverage / fidelity types and method
 * surfaces are ported from quill-admin's own client:
 * /Users/grant/LifeScribe/quill-app/quill-admin/src/services/api.ts
 * (same backend, same routes — kept in sync intentionally).
 */

const BASE = '/api/admin/quill';

// ── Error type ────────────────────────────────────────────────────────────

/**
 * Thrown for any non-2xx response or any `{ success: false }` body (even on
 * a 200, some routes return success:false with a 200 — treat both as errors).
 */
export class QuillApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'QuillApiError';
    this.status = status;
    this.code = code;
  }
}

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

/** Builds a `?a=1&b=2` query string, skipping undefined/null/empty values. */
function qs(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Fetch + JSON. Throws QuillApiError on !ok or `{success:false}`, parsing the
 * `code` field (INVALID_SLOT, RATE_LIMITED, NO_PRIOR_VALUE, NOT_FOUND,
 * MODEL_NOT_IN_CATALOG, NO_GROK_KEY, INVALID_CONFIG_KEY, MISSING_PROMPT_BODY,
 * etc.) off the JSON body when present.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — fall through, handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }

  return (body ?? {}) as T;
}

/** Unwrap the `{ success, data }` envelope → data. Falls back to the raw body
 * when no `data` key is present (several routes reply flat). */
function unwrap<T>(body: unknown): T {
  const b = body as { data?: T } | null | undefined;
  if (b && typeof b === 'object' && 'data' in b && b.data !== undefined) {
    return b.data as T;
  }
  return body as T;
}

// ════════════════════════════════════════════════════════════════════════
// Matrix / dimension types — ported verbatim from quill-admin's api.ts
// ════════════════════════════════════════════════════════════════════════

export interface WeightCell {
  channel: string;
  dimension_key: string;
  weight: number;
}

export interface DimensionMeta {
  dimension_key: string;
  display_name: string;
  is_charter_sensitive: boolean;
  decay_half_life_days: number;
  importance: number;
}

export interface WeightMatrix {
  weight_version: number | null;
  set_by: string | null;
  /**
   * Why this version was saved (migration 572). `null` for versions written
   * before the column existed and for machine-written ('learned') versions —
   * never back-filled, so null means "no rationale was recorded", not "empty".
   */
  edit_note: string | null;
  cells: WeightCell[];
  dimensions: DimensionMeta[];
  /**
   * False on a database where migration 572 has not been applied yet. The
   * difference matters: `edit_note: null` with the column present means nobody
   * wrote a note; with the column absent it means the note could not be stored.
   */
  note_column_present?: boolean;
}

export interface WeightHistoryRow {
  weight_version: number;
  set_by: string;
  is_active: boolean;
  created_at: string;
  cell_count: number;
  /** See WeightMatrix.edit_note. */
  edit_note: string | null;
}

/**
 * POST /weights/version result. `note_persisted` is the honest half: a note the
 * operator typed is dropped when migration 572 has not landed, and the UI must
 * say so rather than implying it was kept.
 */
export interface WeightVersionCreated {
  weight_version: number;
  is_active: boolean;
  edit_note: string | null;
  note_persisted: boolean;
  note_dropped_reason?: string;
}

export interface PromotionStatus {
  eligible?: boolean;
  reason?: string;
  [key: string]: unknown;
}

// ── Interview Question Bank types (routes/interviewBank.js) ───────────────

export type Sensitivity = 'none' | 'personal' | 'charter_sensitive';
export type ExpectedDepth = 'fact' | 'reflection' | 'story';

export interface InterviewQuestion {
  question_id: string;
  bank_version: number;
  module: string;
  question_text: string;
  follow_ups: string[];
  dimension_keys: string[];
  priority: number | null;
  sensitivity: Sensitivity;
  expected_depth: ExpectedDepth;
  consent_preamble: string | null;
  is_active: boolean;
}

export interface QuestionListResponse {
  bank_version: number | null;
  questions: InterviewQuestion[];
}

export type QuestionInput = Partial<Omit<InterviewQuestion, 'question_id' | 'bank_version'>>;

export interface QuestionListParams {
  module?: string;
  dimension_key?: string;
  bank_version?: number;
  include_inactive?: boolean;
}

// ── Coverage dashboard types ────────────────────────────────────────────

export interface CoverageSummaryRow {
  member_id: string;
  completeness: number | null;
  gap_count: number;
  scored_count: number;
  last_activity: string | null;
}

export interface DimensionCoverage {
  dimension_key: string;
  display_name: string;
  coverage: number;
  label: string;
}

export interface CoverageQuestion {
  question_id: string;
  module: string;
  question_text: string;
  dimension_keys: string[];
  priority: number | null;
  sensitivity: string;
  expected_depth: string;
  coverage: number;
  answered_via: string | null;
  answer_summary: string | null;
  last_evaluated_at: string | null;
  citations: unknown[];
}

export interface MemberCoverage {
  member_id: string;
  completeness: number | null;
  readiness: number | null;
  gap_count: number | null;
  dimensions: DimensionCoverage[];
  questions: CoverageQuestion[];
}

// ── Mirror Fidelity dashboard types (routes/mirrorFidelity.js) ────────────

export interface FidelitySummaryRow {
  member_id: string;
  readiness: number | null;
  completeness: number | null;
  converged: boolean | null;
  facts_count: number;
  evidence_count: number;
  persona_version: number | null;
  persona_weight_version: number | null;
  likeness_avg_30d: number | null;
  likeness_n_30d: number;
  mirror_conversations: number;
  mirror_messages: number;
  last_activity: string | null;
}

export interface LikenessTrendPoint {
  date: string;
  avg: number;
  n: number;
}

export type LikenessSeriesKey = 'member' | 'family' | 'mirror_critic' | 'nightly_harness';
export type LikenessTrendSeries = Record<LikenessSeriesKey, LikenessTrendPoint[]>;

export interface EvidenceChannelCount {
  channel: string;
  count: number;
}

export interface PersonaHistoryRow {
  version: number | null;
  weight_version: number | null;
  compile_rationale: unknown;
  is_active: boolean;
  created_at: string | null;
}

export interface ConvergenceStatus {
  converged: boolean;
  completeness: number | null;
  unresolvedSensitiveCount: number;
}

export interface MemberFidelity {
  member_id: string;
  dimensions: DimensionCoverage[];
  convergence: ConvergenceStatus | null;
  likeness_trend: { bucket: string; series: LikenessTrendSeries };
  evidence_by_channel: EvidenceChannelCount[];
  persona_history: PersonaHistoryRow[];
}

export interface PendingWeightCandidate {
  active_weight_version: number | null;
  pending_weight_version: number | null;
  pending_set_by: string | null;
  pending_created_at: string | null;
  pending_cell_count: number | null;
  promotion: PromotionStatus | null;
}

// ════════════════════════════════════════════════════════════════════════
// AI Models types (routes/aiModel.js)
// ════════════════════════════════════════════════════════════════════════

/** Row shape from `quill_xai_models`, as actually SELECTed by readCatalog().
 * Post P3-C, `/models/available` returns BOTH xAI and Anthropic rows, so each
 * row now carries a `provider` ('xai' | 'anthropic') used to filter the
 * Pipeline Models dropdowns by a pipeline's scoping category. */
/**
 * A row of the Quill model catalog (`quill_xai_models`, both providers).
 *
 * Every field below is projected by the backend's readCatalog() — the pipeline
 * dropdowns route on `provider` and gate vision pipelines on `input_modalities`,
 * so these are load-bearing, not decorative. They stay optional in the type only
 * because a legacy row may carry NULL in the column itself.
 */
export interface ModelCatalogRow {
  model_id: string;
  /** 'xai' | 'anthropic' — which dropdown the row belongs in. Absent ⇒ treated as xAI. */
  provider?: string | null;
  /** 'text' | 'vision' — the catalog's own capability class. */
  capability?: string | null;
  tier: string | null;
  aliases: string[] | null;
  pricing_unit: string | null;
  /** USD per pricing_unit of INPUT tokens. */
  pricing_amount: number | null;
  /** USD per pricing_unit of OUTPUT tokens (migration 525). */
  pricing_output_amount?: number | null;
  source?: string | null;
  /** Non-null ⇒ retired. Deprecated rows ARE returned; clients exclude them from options. */
  deprecated_at: string | null;
  /** e.g. ['text'] or ['text','image']. An 'image' entry is what makes a model vision-eligible. */
  input_modalities?: string[] | null;
  output_modalities?: string[] | null;
}

/** GET /models/available and POST /models/refresh both spread `...catalog`
 * (from quillXaiModelsClient.readCatalog()) alongside `success` — flat, with a
 * `data` array — so these two are read directly rather than via `unwrap`. */
export interface ModelCatalogResponse {
  success: true;
  data: ModelCatalogRow[];
  last_synced_at: string | null;
  stale: boolean;
  sync_age_hours: number | null;
}

export interface ModelSyncResult {
  status: string;
  models_found: number;
  models_inserted: number;
  models_updated: number;
  models_deprecated: number;
  duration_ms: number;
  error_code: string | null;
  error_message: string | null;
}

export interface ModelRefreshResponse extends ModelCatalogResponse {
  sync: ModelSyncResult;
}

export type ModelSlot = 'text' | 'text_fast';

export interface ModelDefaultEntry {
  value: string | null;
  source: 'db' | 'env' | 'none';
  updated_by?: string;
  updated_at?: string;
}

export type ModelDefaultsMap = Record<ModelSlot, ModelDefaultEntry>;

export interface SetModelDefaultRequest {
  slot: ModelSlot;
  model_id: string;
  reason?: string;
}

/** PUT /models/defaults replies FLAT: `{success, slot, model_id, old_model}`. */
export interface SetModelDefaultResponse {
  success: true;
  slot: ModelSlot;
  model_id: string;
  old_model: string | null;
}

/** POST /models/rollback replies FLAT: `{success, slot, restored_model}`. */
export interface ModelRollbackResponse {
  success: true;
  slot: ModelSlot;
  restored_model: string;
}

export interface ModelChangeLogRow {
  slot: ModelSlot;
  old_model: string | null;
  new_model: string;
  actor_email: string;
  reason: string | null;
  created_at: string;
}

// ════════════════════════════════════════════════════════════════════════
// Pipeline Models types (routes/pipelineModels.js — P3-C)
// ════════════════════════════════════════════════════════════════════════

/**
 * How a pipeline may be re-pointed:
 *  - selectable                 → any non-deprecated catalog model matching the
 *                                 pipeline's capability (vision pipelines need an
 *                                 image input modality).
 *  - scoped_realtime            → xAI grok-voice* models only (realtime voice).
 *  - scoped_vision_noanthropic  → xAI vision models only; Anthropic excluded.
 *  - scoped_text_xai            → xAI text models only (the vulnerable-user safety
 *                                 path: guardrails_classifier / refusal_generation
 *                                 are hardwired to the xAI endpoint and cannot
 *                                 honor Anthropic).
 *  - fixed                      → immutable (e.g. Titan embeddings — changing it
 *                                 would require re-embedding the whole corpus).
 */
export type PipelineCategory =
  | 'selectable'
  | 'scoped_realtime'
  | 'scoped_vision_noanthropic'
  | 'scoped_text_xai'
  | 'fixed';

/** A {provider, model_id} pair — the shape of both `active` and `default` on a
 * pipeline row. `model_id` is empty ('') only for the theoretical unset case;
 * the backend always resolves both to a concrete model. */
export interface PipelineModelRef {
  provider: string;
  model_id: string;
}

/**
 * A pipeline row (P3-C redesign — shared contract). Two independent model
 * pointers plus a convenience flag:
 *  - `active`  — the model the engine is using right now (set via PUT /:key).
 *  - `default` — the pipeline's default model (set via PUT /:key/default).
 *  - `is_on_default` — true when active === default (Use Default is a no-op).
 * `is_safety_path` marks the two vulnerable-user pipelines whose writes are
 * gated behind the typed confirm_safety step (guardrails_classifier /
 * refusal_generation).
 */
export interface PipelineModel {
  pipeline_key: string;
  display_name: string;
  description: string;
  category: PipelineCategory;
  cost_center: string;
  volume_class: string;
  default_tier: string;
  /** The currently-active model the engine resolves for this pipeline. */
  active: PipelineModelRef;
  /** The pipeline's default model (what Use Default restores active to). */
  default: PipelineModelRef;
  /** True when the active model already equals the default. */
  is_on_default: boolean;
  /** True for guardrails_classifier / refusal_generation (safety gate). */
  is_safety_path?: boolean;
  /**
   * Additive: the counterbalance chains this pipeline is a role in, resolved
   * against the CURRENT selections. Null for the pipelines that are in none.
   */
  counterbalance?: PipelineCounterbalanceMembership[] | null;
  updated_by: string | null;
  updated_at: string | null;
  updated_reason: string | null;
}

/** Body for PUT /:key (set active) and PUT /:key/default (set default). */
export interface SetPipelineModelRequest {
  provider: string;
  model_id: string;
  reason?: string;
  /** Required (true) to re-send a safety-path pipeline write after a 428. */
  confirm_safety?: boolean;
}

/** One row of GET /pipeline-models/:key/history. Rendered defensively — the
 * exact column set is not part of the frozen P3-C contract, so optional. */
export interface PipelineHistoryRow {
  pipeline_key?: string;
  provider: string | null;
  model_id: string | null;
  old_provider?: string | null;
  old_model_id?: string | null;
  temperature?: number | null;
  max_tokens?: number | null;
  source?: string | null;
  actor_email?: string | null;
  updated_by?: string | null;
  reason?: string | null;
  created_at: string;
}

// ════════════════════════════════════════════════════════════════════════
// Counterbalance chains (routes/pipelineModels.js — COUNTERBALANCE_CHAINS)
// ════════════════════════════════════════════════════════════════════════

/**
 * Two roles of one engine chain that are meant to stay apart. `distinct` says
 * at what level: 'provider' when the design puts the two roles on different
 * providers, 'model' when one provider is fine but the same MODEL on both
 * sides is the self-review failure. `collapsed` is the resolved verdict for
 * the CURRENT selections.
 */
export interface PipelineCounterbalancePartner {
  pipeline_key: string;
  display_name: string;
  role: string | null;
  distinct: 'provider' | 'model';
  why: string;
  collapsed: boolean;
  this_model: PipelineModelRef;
  other_model: PipelineModelRef;
}

/** One pipeline's role in one declared chain. */
export interface PipelineCounterbalanceMembership {
  chain_key: string;
  label: string;
  /** The engine file that establishes the arrangement. */
  source: string;
  role: string | null;
  partners: PipelineCounterbalancePartner[];
  /** True when at least one partner pairing has collapsed. */
  collapsed: boolean;
}

// ════════════════════════════════════════════════════════════════════════
// Bulk reset types (routes/pipelineModels.js — POST /reset-all-to-default)
// ════════════════════════════════════════════════════════════════════════

/** Per-row verdict. Every registry row gets exactly one — never a bare success. */
export type PipelineResetOutcome =
  | 'changed'
  | 'already_on_default'
  | 'skipped_immutable'
  | 'skipped_no_default'
  | 'skipped_safety_path'
  | 'failed';

export interface PipelineResetResultRow {
  pipeline_key: string;
  display_name: string;
  category: PipelineCategory;
  active: PipelineModelRef;
  default: PipelineModelRef;
  is_on_default: boolean;
  outcome: PipelineResetOutcome;
  from?: PipelineModelRef;
  to?: PipelineModelRef;
  reason?: string;
  code?: string;
  /** The model moved but its audit row could not be written. */
  change_log_failed?: boolean;
}

export type PipelineResetSummary = Record<PipelineResetOutcome | 'total', number>;

export interface PipelineResetResponse {
  success: true;
  dry_run: boolean;
  action: string;
  summary: PipelineResetSummary;
  results: PipelineResetResultRow[];
  /** 'not_applicable' on a dry run; 'not_needed' when nothing moved. */
  invalidation: 'confirmed' | 'ttl_fallback' | 'not_needed' | 'not_applicable';
}

// ════════════════════════════════════════════════════════════════════════
// Safety reverification types (routes/pipelineModels.js — G3 reminder)
// ════════════════════════════════════════════════════════════════════════

/**
 * One row of GET /safety-reverifications, shaped by the P3 cross-repo contract.
 * The route LEFT JOINs the change log so each row carries `new_model` (what the
 * safety pipeline was changed TO) and `changed_by` (the actor). `id` is the
 * quill_safety_reverifications serial (a number in the JSON); acknowledge()
 * keys off it. Fields other than id/pipeline_key/created_at may be null when the
 * change_log_ref is orphaned, so they are optional and rendered defensively.
 */
export interface SafetyReverification {
  id: string;
  pipeline_key: string;
  change_log_ref?: number | null;
  created_at: string;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  note?: string | null;
  /** The model the safety pipeline was changed to (from the joined change log). */
  new_model?: string | null;
  /** The actor who made the change (change_log.actor_email). */
  changed_by?: string | null;
}

// ════════════════════════════════════════════════════════════════════════
// AI Costs types (routes/costs.js — P3-C)
// ════════════════════════════════════════════════════════════════════════

// The field names below are DB-CANONICAL — they mirror routes/aiCosts.js
// verbatim (est_cost_usd, input_tokens, output_tokens, calls, day). NOTE: the
// numeric aggregates come back from node-pg as strings (SUM()::bigint / numeric
// serialize as text), so every consumer coerces with a Number() guard before
// arithmetic or formatting. Do NOT rename these to a "nicer" shape — the
// backend does not send `cost` / `tokens_in` / `total_cost` / `date`.

export interface CostRangeParams {
  from?: string;
  to?: string;
}

/**
 * Shared filter params accepted by every /costs endpoint (contract P3-C):
 * a date range plus an optional comma-separated cost-center list and an
 * optional provider. `cost_centers` is a COMMA LIST (e.g. "conversation,memory")
 * — the backend splits it; the client sends it pre-joined.
 */
export interface CostQueryParams extends CostRangeParams {
  cost_centers?: string;
  provider?: string;
}

/** The {from, to} echoed back on every cost response. */
export interface CostRange {
  from: string;
  to: string;
}

/** The four aggregate columns every /costs endpoint SELECTs. Numbers may arrive
 * as strings from node-pg — coerce before use. */
export interface CostTotals {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  est_cost_usd: number;
}

export interface CostProviderBreakdown extends CostTotals {
  provider: string;
}

export interface CostCenterBreakdown extends CostTotals {
  cost_center: string;
}

/** GET /costs/summary — FLAT body `{ success, range, totals, by_provider,
 * by_cost_center }` (no `data` key), so getSummary reads the raw body. */
export interface CostSummary {
  range?: CostRange;
  totals: CostTotals;
  by_provider: CostProviderBreakdown[];
  by_cost_center: CostCenterBreakdown[];
}

/** One row of GET /costs/daily (`data` array). Keyed on `day`, not `date`. */
export interface CostDailyPoint extends CostTotals {
  day: string;
}

/** One row of GET /costs/pipelines (`data` array). The backend groups by
 * pipeline_key/cost_center/provider — there is NO display_name or model_id
 * column, so the table renders the key + provider + cost center it does send. */
export interface CostPipelineRow extends CostTotals {
  pipeline_key: string;
  cost_center?: string | null;
  provider?: string | null;
  avg_latency_ms: number | null;
  error_rate: number | null;
}

/** One row of GET /costs/members (`data` array). */
export interface CostMemberRow extends CostTotals {
  member_id: string;
}

export interface CostDailyParams extends CostQueryParams {
  /** Legacy single cost-center filter (still accepted); prefer `cost_centers`. */
  cost_center?: string;
}

/** GET /costs/runs — FLAT body `{ success, run_id, totals, by_pipeline }`. */
export interface CostRunPipelineRow extends CostTotals {
  pipeline_key: string;
  provider?: string | null;
}

export interface CostRunResponse {
  run_id: string;
  totals: CostTotals;
  by_pipeline: CostRunPipelineRow[];
}

// ════════════════════════════════════════════════════════════════════════
// Guardrails types (routes/guardrails.js)
// ════════════════════════════════════════════════════════════════════════

export interface CapabilityTier {
  tier_id: string;
  tier_name: string;
  tier_description: string | null;
  capability_categories: unknown;
  classifier_model: string | null;
  classifier_temperature: number | null;
  fast_path_enabled: boolean;
  decision_log_enabled: boolean;
  is_active: boolean;
  available_to_subscription_tiers: string[] | null;
  member_count: number;
}

export type TierPatch = Partial<
  Pick<
    CapabilityTier,
    | 'tier_name'
    | 'tier_description'
    | 'capability_categories'
    | 'classifier_model'
    | 'classifier_temperature'
    | 'fast_path_enabled'
    | 'decision_log_enabled'
    | 'is_active'
    | 'available_to_subscription_tiers'
  >
>;

export interface MemberTierAssignment {
  quill_member_id: string;
  tier_id: string;
  tier_name: string;
  capability_categories: unknown;
  assigned_by_admin_id: string | null;
  assignment_reason: string | null;
  expires_at: string | null;
  assigned_at: string;
  updated_at: string;
}

export interface SetMemberTierRequest {
  tier_id: string;
  reason?: string;
  expires_at?: string;
}

// Rules-table CHECK constraint (migration 474): what a rule can be authored as.
export type GuardrailMatchType = 'fast_path_keyword' | 'fast_path_embedding' | 'classifier_required';
// Decisions-log CHECK constraint (migration 475): what the live engine records.
export type GuardrailDecisionMatchType =
  | 'fast_path_keyword'
  | 'fast_path_embedding'
  | 'classifier_llm'
  | 'admin_override';
export type GuardrailVerdict = string;

export interface GuardrailRule {
  rule_id: string;
  rule_key: string;
  rule_name: string;
  rule_description?: string | null;
  category: string;
  match_type: GuardrailMatchType;
  keywords: string[] | null;
  regex_patterns: string[] | null;
  verdict: GuardrailVerdict;
  priority: number;
  refusal_template_id: string | null;
  is_active: boolean;
  created_by_admin_id?: string | null;
  last_modified_by?: string | null;
}

export interface CreateGuardrailRuleRequest {
  rule_key: string;
  rule_name: string;
  category: string;
  match_type: GuardrailMatchType;
  keywords?: string[];
  regex_patterns?: string[];
  verdict: GuardrailVerdict;
  priority?: number;
  refusal_template_id?: string;
}

export type UpdateGuardrailRuleRequest = Partial<
  Pick<
    GuardrailRule,
    'is_active' | 'keywords' | 'regex_patterns' | 'verdict' | 'priority' | 'rule_name' | 'rule_description'
  >
>;

export interface RefusalTemplate {
  template_id: string;
  template_key: string;
  body: string;
  future_hint: string | null;
  is_active: boolean;
}

export interface UpdateRefusalTemplateRequest {
  body?: string;
  future_hint?: string;
  is_active?: boolean;
}

export interface GuardrailDecision {
  decision_id: string;
  quill_member_id: string;
  query_text_hash: string;
  query_length: number;
  verdict: GuardrailVerdict;
  category: string | null;
  match_type: GuardrailDecisionMatchType;
  classifier_latency_ms: number | null;
  classifier_tokens: number | null;
  classifier_confidence: number | null;
  allowed_to_proceed: boolean;
  tier_id: string | null;
  session_id: string | null;
  conversation_turn: number | null;
  occurred_at: string;
}

export interface DecisionsQueryParams {
  member_id?: string;
  verdict?: string;
  category?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface GuardrailTestRequest {
  query_text: string;
  tier_id?: string;
}

export interface GuardrailTestResponse {
  success: true;
  verdict: string;
  // The /test endpoint simulates the fast path only: it either matches a
  // fast_path_keyword rule or reports 'none' (query would proceed to the LLM).
  match_type: 'none' | 'fast_path_keyword';
  matched_rule: { rule_key: string; category: string; priority: number } | null;
  refusal_preview: string | null;
  note: string;
}

export interface GuardrailReloadResponse {
  success: true;
  note: string;
}

// ════════════════════════════════════════════════════════════════════════
// Reasoning config types (routes/reasoningConfig.js)
// ════════════════════════════════════════════════════════════════════════

// The keys the quill-engine actually reads (verified against engine source +
// live DB rows 2026-07-24; matches quill-admin-backend VALID_CONFIG_KEYS).
export type ReasoningConfigKey =
  | 'constitutional_classifier_prompt'
  | 'refusal_generation_prompt'
  | 'fuzzy_interpretation_prompt'
  | 'story_completion_classifier_prompt'
  | 'interview_coverage_judge_prompt'
  | 'mirror_listener_prompt';

export interface ReasoningConfigRow {
  config_id: string;
  config_key: ReasoningConfigKey;
  config_name: string;
  prompt_body: string;
  version: number;
  model: string;
  temperature: number;
  max_tokens: number;
  is_active: boolean;
  edited_by_admin_id: string | null;
  edit_note: string | null;
  created_at: string;
}

export interface UpdateReasoningConfigRequest {
  prompt_body: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  edit_note?: string;
}

export interface ReasoningTestRequest {
  draft_prompt: string;
  config_key: ReasoningConfigKey;
  test_queries: string[];
}

export interface ReasoningTestVerdict {
  verdict: string;
  category?: string;
  is_emotional_or_vulnerable?: boolean;
  confidence: number;
}

export interface ReasoningTestResult {
  query: string;
  draft: ReasoningTestVerdict;
  active: ReasoningTestVerdict | null;
  changed: boolean | null;
}

export interface ReasoningTestResponse {
  success: true;
  results: ReasoningTestResult[];
}

const MAX_REASONING_TEST_QUERIES = 20;

// ════════════════════════════════════════════════════════════════════════
// Health
// ════════════════════════════════════════════════════════════════════════

/** One entry of the `/api/admin/quill/health` aggregation (quill-engine /
 * quill-auth-bridge upstream checks). */
export interface QuillUpstreamHealth {
  name: string;
  ok: boolean;
  status?: number;
  error?: string;
  [key: string]: unknown;
}

/** Actual shape of `GET /api/admin/quill/health` (verified against
 * server.js) — an aggregation of upstream service checks, NOT a bare
 * `{ok:true}`. */
export interface QuillHealthCheckResponse {
  success: true;
  services: QuillUpstreamHealth[];
}

export type QuillBackendStatus = 'up' | 'down' | 'unauthorized';

export interface QuillHealthCheckResult {
  status: QuillBackendStatus;
  detail?: QuillHealthCheckResponse | null;
  error?: string;
}

// ════════════════════════════════════════════════════════════════════════
// quillApi
// ════════════════════════════════════════════════════════════════════════

export const quillApi = {
  // ── AI model selector (routes/aiModel.js) ────────────────────────────
  models: {
    /** GET /models/available — full envelope incl. sync metadata, read raw (not unwrapped). */
    async getAvailable(): Promise<ModelCatalogResponse> {
      return request<ModelCatalogResponse>('/models/available');
    },

    /** POST /models/refresh — 429 RATE_LIMITED w/ retry_after_seconds on the thrown QuillApiError's body is not surfaced structurally; catch and inspect `code`. */
    async refresh(): Promise<ModelRefreshResponse> {
      return request<ModelRefreshResponse>('/models/refresh', { method: 'POST' });
    },

    /** GET /models/current — flat `{success, defaults}`, no `data` key, so
     * `unwrap` returns the whole body; pull `.defaults` out here. */
    async getCurrent(): Promise<ModelDefaultsMap> {
      const body = await request<{ success: true; defaults: ModelDefaultsMap }>('/models/current');
      return unwrap<{ success: true; defaults: ModelDefaultsMap }>(body).defaults;
    },

    async setDefault(payload: SetModelDefaultRequest): Promise<SetModelDefaultResponse> {
      const body = await request<SetModelDefaultResponse>('/models/defaults', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return unwrap<SetModelDefaultResponse>(body);
    },

    async rollback(slot: ModelSlot): Promise<ModelRollbackResponse> {
      const body = await request<ModelRollbackResponse>('/models/rollback', {
        method: 'POST',
        body: JSON.stringify({ slot }),
      });
      return unwrap<ModelRollbackResponse>(body);
    },

    async getHistory(params: { slot?: ModelSlot; limit?: number } = {}): Promise<ModelChangeLogRow[]> {
      const body = await request<{ success: true; data: ModelChangeLogRow[] }>(
        `/models/history${qs(params)}`
      );
      return unwrap<ModelChangeLogRow[]>(body);
    },
  },

  // ── Guardrails management (routes/guardrails.js) ──────────────────────
  guardrails: {
    async getTiers(): Promise<CapabilityTier[]> {
      const body = await request<{ success: true; data: CapabilityTier[] }>('/guardrails/tiers');
      return unwrap<CapabilityTier[]>(body);
    },

    async updateTier(tierId: string, patch: TierPatch): Promise<void> {
      await request<{ success: true }>(`/guardrails/tiers/${encodeURIComponent(tierId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },

    /** 404 NOT_FOUND when the member has no tier assignment yet. */
    async getMemberTier(memberId: string): Promise<MemberTierAssignment> {
      const body = await request<{ success: true; data: MemberTierAssignment }>(
        `/guardrails/members/${encodeURIComponent(memberId)}/tier`
      );
      return unwrap<MemberTierAssignment>(body);
    },

    async setMemberTier(memberId: string, payload: SetMemberTierRequest): Promise<void> {
      await request<{ success: true }>(`/guardrails/members/${encodeURIComponent(memberId)}/tier`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    async getRules(): Promise<GuardrailRule[]> {
      const body = await request<{ success: true; data: GuardrailRule[] }>('/guardrails/rules');
      return unwrap<GuardrailRule[]>(body);
    },

    async createRule(payload: CreateGuardrailRuleRequest): Promise<GuardrailRule> {
      const body = await request<{ success: true; data: GuardrailRule }>('/guardrails/rules', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return unwrap<GuardrailRule>(body);
    },

    async updateRule(ruleId: string, patch: UpdateGuardrailRuleRequest): Promise<void> {
      await request<{ success: true }>(`/guardrails/rules/${encodeURIComponent(ruleId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },

    async getTemplates(): Promise<RefusalTemplate[]> {
      const body = await request<{ success: true; data: RefusalTemplate[] }>('/guardrails/templates');
      return unwrap<RefusalTemplate[]>(body);
    },

    async updateTemplate(templateId: string, patch: UpdateRefusalTemplateRequest): Promise<void> {
      await request<{ success: true }>(`/guardrails/templates/${encodeURIComponent(templateId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },

    async getDecisions(params: DecisionsQueryParams = {}): Promise<GuardrailDecision[]> {
      const body = await request<{ success: true; data: GuardrailDecision[] }>(
        `/guardrails/decisions${qs(params as Record<string, string | number | undefined>)}`
      );
      return unwrap<GuardrailDecision[]>(body);
    },

    async test(payload: GuardrailTestRequest): Promise<GuardrailTestResponse> {
      return request<GuardrailTestResponse>('/guardrails/test', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    async reload(): Promise<GuardrailReloadResponse> {
      return request<GuardrailReloadResponse>('/guardrails/reload', { method: 'POST' });
    },
  },

  // ── Reasoning config / constitutional prompts (routes/reasoningConfig.js) ─
  reasoning: {
    async getConfig(): Promise<ReasoningConfigRow[]> {
      const body = await request<{ success: true; data: ReasoningConfigRow[] }>('/reasoning/config');
      return unwrap<ReasoningConfigRow[]>(body);
    },

    /** 400 INVALID_CONFIG_KEY if `key` isn't one of the five known keys. */
    async getConfigHistory(key: ReasoningConfigKey): Promise<ReasoningConfigRow[]> {
      const body = await request<{ success: true; data: ReasoningConfigRow[] }>(
        `/reasoning/config/${encodeURIComponent(key)}/history`
      );
      return unwrap<ReasoningConfigRow[]>(body);
    },

    /** 400 MISSING_PROMPT_BODY / INVALID_CONFIG_KEY. */
    async updateConfig(key: ReasoningConfigKey, payload: UpdateReasoningConfigRequest): Promise<ReasoningConfigRow> {
      const body = await request<{ success: true; data: ReasoningConfigRow }>(
        `/reasoning/config/${encodeURIComponent(key)}`,
        { method: 'POST', body: JSON.stringify(payload) }
      );
      return unwrap<ReasoningConfigRow>(body);
    },

    /** 404 VERSION_NOT_FOUND. */
    async restoreConfig(key: ReasoningConfigKey, version: number): Promise<ReasoningConfigRow> {
      const body = await request<{ success: true; data: ReasoningConfigRow }>(
        `/reasoning/config/${encodeURIComponent(key)}/restore/${version}`,
        { method: 'POST' }
      );
      return unwrap<ReasoningConfigRow>(body);
    },

    /** 503 NO_GROK_KEY if the backend has no Grok credentials configured.
     * Client-side caps test_queries at 20 (matches the backend's own
     * `.slice(0, 20)`) and throws BEFORE hitting the network if exceeded. */
    async test(payload: ReasoningTestRequest): Promise<ReasoningTestResponse> {
      if (payload.test_queries.length > MAX_REASONING_TEST_QUERIES) {
        throw new QuillApiError(
          `Too many test queries (${payload.test_queries.length}). Maximum is ${MAX_REASONING_TEST_QUERIES}.`,
          400,
          'TOO_MANY_TEST_QUERIES'
        );
      }
      return request<ReasoningTestResponse>('/reasoning/test', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  },

  // ── PWE channel-weight matrix editor (routes/channelWeights.js) ───────
  weights: {
    async getActiveMatrix(): Promise<WeightMatrix> {
      const body = await request<{ success: true; data: WeightMatrix }>('/weights');
      return unwrap<WeightMatrix>(body);
    },

    async getVersion(version: number): Promise<WeightMatrix> {
      const body = await request<{ success: true; data: WeightMatrix }>(`/weights/${version}`);
      return unwrap<WeightMatrix>(body);
    },

    async getHistory(): Promise<WeightHistoryRow[]> {
      const body = await request<{ success: true; data: WeightHistoryRow[] }>('/weights/history');
      return unwrap<WeightHistoryRow[]>(body);
    },

    /**
     * @param note  Operator rationale, persisted to quill_channel_weights.edit_note
     *   (migration 572). Omit or pass an empty string for no note. The response's
     *   `note_persisted` says whether it was actually stored.
     */
    async createVersion(cells: WeightCell[], note?: string): Promise<WeightVersionCreated> {
      const body = await request<{ success: true; data: WeightVersionCreated }>(
        '/weights/version',
        { method: 'POST', body: JSON.stringify({ cells, note: note ?? null }) }
      );
      return unwrap(body);
    },

    async activateVersion(
      version: number
    ): Promise<{ weight_version: number; effective_within_seconds: number }> {
      const body = await request<{
        success: true;
        data: { weight_version: number; effective_within_seconds: number };
      }>(`/weights/${version}/activate`, { method: 'POST' });
      return unwrap(body);
    },

    async rollback(): Promise<{
      weight_version: number;
      rolled_back_from: number;
      effective_within_seconds: number;
    }> {
      const body = await request<{
        success: true;
        data: { weight_version: number; rolled_back_from: number; effective_within_seconds: number };
      }>('/weights/rollback', { method: 'POST' });
      return unwrap(body);
    },

    async getPromotionStatus(version: number): Promise<PromotionStatus> {
      const body = await request<{ success: true; data: PromotionStatus }>(
        `/weights/${version}/promotion-status`
      );
      return unwrap<PromotionStatus>(body);
    },
  },

  // ── Interview Question Bank editor (routes/interviewBank.js) ──────────
  interview: {
    async listQuestions(params: QuestionListParams = {}): Promise<QuestionListResponse> {
      const body = await request<{ success: true; data: QuestionListResponse }>(
        `/interview/questions${qs(params as Record<string, string | number | boolean | undefined>)}`
      );
      return unwrap<QuestionListResponse>(body);
    },

    async getQuestion(id: string): Promise<InterviewQuestion> {
      const body = await request<{ success: true; data: InterviewQuestion }>(
        `/interview/questions/${encodeURIComponent(id)}`
      );
      return unwrap<InterviewQuestion>(body);
    },

    async createQuestion(payload: QuestionInput): Promise<InterviewQuestion> {
      const body = await request<{ success: true; data: InterviewQuestion }>('/interview/questions', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return unwrap<InterviewQuestion>(body);
    },

    async updateQuestion(id: string, payload: QuestionInput): Promise<InterviewQuestion> {
      const body = await request<{ success: true; data: InterviewQuestion }>(
        `/interview/questions/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify(payload) }
      );
      return unwrap<InterviewQuestion>(body);
    },

    async deleteQuestion(id: string): Promise<InterviewQuestion> {
      const body = await request<{ success: true; data: InterviewQuestion }>(
        `/interview/questions/${encodeURIComponent(id)}`,
        { method: 'DELETE' }
      );
      return unwrap<InterviewQuestion>(body);
    },
  },

  // ── Per-member coverage dashboard (routes/interviewBank.js) ───────────
  coverage: {
    async getSummary(): Promise<CoverageSummaryRow[]> {
      const body = await request<{ success: true; data: CoverageSummaryRow[] }>(
        '/interview/coverage/summary'
      );
      return unwrap<CoverageSummaryRow[]>(body);
    },

    async getMemberCoverage(memberId: string): Promise<MemberCoverage> {
      const body = await request<{ success: true; data: MemberCoverage }>(
        `/interview/coverage/${encodeURIComponent(memberId)}`
      );
      return unwrap<MemberCoverage>(body);
    },
  },

  // ── Mirror Fidelity dashboard (routes/mirrorFidelity.js) ───────────────
  fidelity: {
    async getSummary(): Promise<FidelitySummaryRow[]> {
      const body = await request<{ success: true; data: FidelitySummaryRow[] }>('/fidelity/summary');
      return unwrap<FidelitySummaryRow[]>(body);
    },

    async getMemberFidelity(memberId: string, bucket?: 'day' | 'week'): Promise<MemberFidelity> {
      const body = await request<{ success: true; data: MemberFidelity }>(
        `/fidelity/${encodeURIComponent(memberId)}${qs(bucket ? { bucket } : undefined)}`
      );
      return unwrap<MemberFidelity>(body);
    },

    /** The pending LEARNED weight candidate + its promotion eligibility. Composed
     * server-side from the engine's own gate — the frontend never re-derives
     * "which version is waiting" or "may it be promoted". */
    async getPendingWeightCandidate(): Promise<PendingWeightCandidate> {
      const body = await request<{ success: true; data: PendingWeightCandidate }>(
        '/fidelity/weights/pending'
      );
      return unwrap<PendingWeightCandidate>(body);
    },
  },

  // ── Pipeline model selector (routes/pipelineModels.js — P3-C) ─────────
  pipelineModels: {
    /** GET /pipeline-models — one row per pipeline_key with its active + default
     * model and the is_on_default convenience flag. */
    async list(): Promise<PipelineModel[]> {
      const body = await request<{ success: true; data: PipelineModel[] }>('/pipeline-models');
      return unwrap<PipelineModel[]>(body);
    },

    /** PUT /pipeline-models/:key — set the ACTIVE model. Throws QuillApiError on:
     *  400 IMMUTABLE_PIPELINE (fixed rows) / scoping errors / MODEL_NOT_IN_CATALOG,
     *  428 SAFETY_CONFIRMATION_REQUIRED (re-send with confirm_safety:true). */
    async setActive(pipelineKey: string, payload: SetPipelineModelRequest): Promise<void> {
      await request<{ success: true }>(`/pipeline-models/${encodeURIComponent(pipelineKey)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    },

    /** PUT /pipeline-models/:key/default — set the DEFAULT model. Same validation
     * + 428 safety gate as setActive. */
    async setDefault(pipelineKey: string, payload: SetPipelineModelRequest): Promise<void> {
      await request<{ success: true }>(
        `/pipeline-models/${encodeURIComponent(pipelineKey)}/default`,
        { method: 'PUT', body: JSON.stringify(payload) }
      );
    },

    /** POST /pipeline-models/:key/use-default — set active = default. Safety-path
     * pipelines return 428 unless `confirmSafety` is passed. No-op when the
     * pipeline is already on its default (button is disabled in that state). */
    async useDefault(pipelineKey: string, confirmSafety = false): Promise<void> {
      await request<{ success: true }>(
        `/pipeline-models/${encodeURIComponent(pipelineKey)}/use-default`,
        {
          method: 'POST',
          body: JSON.stringify(confirmSafety ? { confirm_safety: true } : {}),
        }
      );
    },

    /** POST /pipeline-models/:key/revert — restore the PREVIOUS model recorded in
     * the change log. Safety-path pipelines (guardrails_classifier /
     * refusal_generation) return 428 unless `confirmSafety` is passed. */
    async revert(pipelineKey: string, confirmSafety = false): Promise<void> {
      await request<{ success: true }>(
        `/pipeline-models/${encodeURIComponent(pipelineKey)}/revert`,
        {
          method: 'POST',
          body: JSON.stringify(confirmSafety ? { confirm_safety: true } : {}),
        }
      );
    },

    async getHistory(pipelineKey: string): Promise<PipelineHistoryRow[]> {
      const body = await request<{ success: true; data: PipelineHistoryRow[] }>(
        `/pipeline-models/${encodeURIComponent(pipelineKey)}/history`
      );
      return unwrap<PipelineHistoryRow[]>(body);
    },

    /**
     * POST /pipeline-models/reset-all-to-default — return every ELIGIBLE
     * pipeline to its own stored default.
     *
     * `dryRun` runs the identical planner and writes nothing, so the confirm
     * dialog shows exactly what the real call would do. Fixed rows and the two
     * safety-path pipelines are excluded by the server and reported as skipped
     * with a reason; the full per-row result comes back either way.
     */
    async resetAllToDefault(
      { dryRun = false, reason }: { dryRun?: boolean; reason?: string } = {}
    ): Promise<PipelineResetResponse> {
      return request<PipelineResetResponse>('/pipeline-models/reset-all-to-default', {
        method: 'POST',
        body: JSON.stringify({ dry_run: dryRun, ...(reason ? { reason } : {}) }),
      });
    },
  },

  // ── Safety reverifications (routes/pipelineModels.js — G3 reminder) ────
  safety: {
    /** GET /safety-reverifications?pending=true — outstanding vulnerable-user
     * re-verification reminders raised when a safety-path model was changed. */
    async listPending(): Promise<SafetyReverification[]> {
      const body = await request<{ success: true; data: SafetyReverification[] }>(
        '/safety-reverifications?pending=true'
      );
      return unwrap<SafetyReverification[]>(body);
    },

    /** POST /safety-reverifications/:id/acknowledge {note}. */
    async acknowledge(id: string, note: string): Promise<void> {
      await request<{ success: true }>(
        `/safety-reverifications/${encodeURIComponent(id)}/acknowledge`,
        { method: 'POST', body: JSON.stringify({ note }) }
      );
    },
  },

  // ── AI cost ledger (routes/costs.js — P3-C) ───────────────────────────
  costs: {
    /** GET /costs/summary replies FLAT (`{success, range, totals, by_provider,
     * by_cost_center}`) — no `data` key — so `unwrap` returns the whole body.
     * Accepts `cost_centers` (comma list) + `provider` to scope the whole page. */
    async getSummary(params: CostQueryParams = {}): Promise<CostSummary> {
      const body = await request<CostSummary>(`/costs/summary${qs(params)}`);
      return unwrap<CostSummary>(body);
    },

    async getDaily(params: CostDailyParams = {}): Promise<CostDailyPoint[]> {
      const body = await request<{ success: true; data: CostDailyPoint[] }>(
        `/costs/daily${qs(params)}`
      );
      return unwrap<CostDailyPoint[]>(body);
    },

    async getPipelines(params: CostQueryParams = {}): Promise<CostPipelineRow[]> {
      const body = await request<{ success: true; data: CostPipelineRow[] }>(
        `/costs/pipelines${qs(params)}`
      );
      return unwrap<CostPipelineRow[]>(body);
    },

    async getMembers(params: CostQueryParams & { limit?: number } = {}): Promise<CostMemberRow[]> {
      const body = await request<{ success: true; data: CostMemberRow[] }>(
        `/costs/members${qs(params)}`
      );
      return unwrap<CostMemberRow[]>(body);
    },

    /** GET /costs/runs replies FLAT (`{success, run_id, totals, by_pipeline}`) —
     * no `data` key — so `unwrap` returns the whole body. */
    async getRun(runId: string): Promise<CostRunResponse> {
      const body = await request<CostRunResponse>(`/costs/runs${qs({ run_id: runId })}`);
      return unwrap<CostRunResponse>(body);
    },
  },

  // ── Health ──────────────────────────────────────────────────────────
  health: {
    /** GET /api/admin/quill/health with a 3s client-side timeout. Never throws —
     * returns a status discriminant the caller (QuillHealthContext) renders on. */
    async check(): Promise<QuillHealthCheckResult> {
      const controller = new AbortController();
      // 8s, not 3s: in production this call traverses the admin ALB, adminAuth's
      // DB session lookup, the cross-VPC proxy hop, and quill-admin-backend's own
      // health aggregation (which itself fans out to engine + auth-bridge). A 3s
      // budget aborts that legitimately-slow chain and false-reports 'down'.
      const timer = setTimeout(() => controller.abort(), 8000);
      try {
        const res = await fetch(`${BASE}/health`, { signal: controller.signal });
        clearTimeout(timer);

        if (res.status === 401) {
          return { status: 'unauthorized' };
        }
        if (!res.ok) {
          return { status: 'down', error: `HTTP ${res.status}` };
        }
        const body = (await res.json().catch(() => null)) as QuillHealthCheckResponse | null;
        if (!body || body.success !== true) {
          return { status: 'down', error: 'Malformed health response' };
        }
        return { status: 'up', detail: body };
      } catch (e) {
        clearTimeout(timer);
        return {
          status: 'down',
          error: e instanceof Error ? e.message : 'Could not reach Quill backend',
        };
      }
    },
  },
};

export default quillApi;
